import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import { InventoryListingStatus } from '@ekhodealer/ekho-common';
import { SkeletonRect, Stack } from '@ekhodealer/ekho-common/rev';

import type { FiltersDrawerCheckboxGroupItem } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';
import type { PublishStatusOption } from '../../../atoms/inventoryAtoms';

import { AccordionSection } from '@common/components/AccordionSection';
import { FiltersDrawerCheckboxGroup } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { publishStatusFilterAtom, updatePublishStatusFilterAtom } from '../../../atoms/inventoryAtoms';
import { useInventoryFilterFacetCounts } from '../../../hooks/useInventoryFilterFacetCounts';


const PUBLISH_STATUS_OPTIONS = [
	{ value: 'published', label: 'Published' },
	{ value: 'draft', label: 'Draft' },
	{ value: 'hidden', label: 'Hidden' },
];

/**
 * Maps backend listing.status enum values to frontend publishStatus values
 * published -> PUBLISHED
 * draft -> DRAFT, INCOMPLETE_DRAFT, LOCKED (sum of all)
 * hidden -> HIDDEN
 */
const getPublishStatusCount = (
	listingStatusCounts: Record<string, number>,
	frontendValue: string,
): number => {
	switch (frontendValue) {
		case 'published':
			return listingStatusCounts[InventoryListingStatus.PUBLISHED] || 0;
		case 'draft':
			return (
				(listingStatusCounts[InventoryListingStatus.DRAFT] || 0) +
				(listingStatusCounts[InventoryListingStatus.INCOMPLETE_DRAFT] || 0) +
				(listingStatusCounts[InventoryListingStatus.LOCKED] || 0)
			);
		case 'hidden':
			return listingStatusCounts[InventoryListingStatus.HIDDEN] || 0;
		default:
			return 0;
	}
};

export function PublishStatusSection() {
	const publishStatus = useAtomValue(publishStatusFilterAtom);
	const setPublishStatus = useSetAtom(updatePublishStatusFilterAtom);

	// Get facet counts from the API response
	const { listingStatusCounts, isLoading } = useInventoryFilterFacetCounts();

	// Build publish status items with counts from facets
	const publishStatusItems: FiltersDrawerCheckboxGroupItem[] = useMemo(() => {
		return PUBLISH_STATUS_OPTIONS.map((option) => ({
			value: option.value,
			label: option.label,
			count: getPublishStatusCount(listingStatusCounts, option.value),
		}));
	}, [listingStatusCounts]);

	// Wrapper function to convert string[] to PublishStatusOption[]
	const handleChange = (values: string[]) => {
		setPublishStatus(values as PublishStatusOption[]);
	};

	return (
		<AccordionSection title='Publish status' defaultExpanded>
			{isLoading ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='100%' height='24px' />
					<SkeletonRect width='100%' height='24px' />
					<SkeletonRect width='100%' height='24px' />
				</Stack>
			) : (
				<FiltersDrawerCheckboxGroup
					items={publishStatusItems}
					value={publishStatus}
					onChange={handleChange}
				/>
			)}
		</AccordionSection>
	);
}
